import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import ImageInput from "../../../components/imageInput";
import SelectComponent from "../../../components/selectComponent";
import TextFieldCompnent from "../../../components/textFieldComponent";
import RadioButtonComponent from "../../../components/radioButtonComponent";
import Reorder from "../../../components/reorder";
import ButtonComponent from "../../../components/ButtonComponent";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import SelectListUI from "../../../components/selectListUI";
import DatepickerFilter from "../../../components/filters/datepickerFilter";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const ProductForm = ({
  buttonText,
  handleFormSubmit,
  initialValues,
  categories,
  allergies,
  previewImage,
  setPreviewImage,
  selectedTypes,
  handleTypeSelect,
  typesWithIngrediants,
  typeVariation,
  setSelectedTypes,
  loading,
  image,
  isDialog,
}) => {
  const { t } = useTranslation();
  const { currency } = useRestaurant();
  const isNonMobile = useMediaQuery("(min-width:900px)");
  const todayCheck = new Date();
  todayCheck.setHours(0, 0, 0, 0);

  const productSchema = yup.object().shape({
    name: yup.string().required("Nom est requis"),
    description: yup.string(),
    // category: yup.string().required("categorie est requis"),
    categories: yup
      .array()
      .min(1, "Au moins une catégorie est requise")
      .of(yup.string().required()),
    price: yup.number().required("Prix est requis"),
    formulePrice: yup.number(),
    choice: yup.string().required("Choix est requis"),
    outOfStock: yup.boolean(),
    visible: yup.boolean(),
    typeVariation: yup.string(),
    allergies: yup.array(),
    discountValue: yup
      .number()
      .nullable()
      .lessThan(yup.ref("price"), "La remise doit être inférieure au prix"),
    discountStartDate: yup
      .mixed()
      .nullable()
      .notRequired()
      .test(
        "start-date-min",
        "La date de début doit être aujourd'hui ou plus tard",
        function (value) {
          if (!value) return true;
          const start = new Date(value);
          start.setHours(0, 0, 0, 0);
          return start >= todayCheck;
        }
      ),
    discountEndDate: yup
      .mixed()
      .nullable()
      .notRequired()
      .test(
        "end-date-after-start",
        "La date de fin doit être après la date de début",
        function (value) {
          const { discountStartDate } = this.parent;
          if (!value || !discountStartDate) return true;
          const start = new Date(discountStartDate);
          const end = new Date(value);
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          return end > start;
        }
      ),
    tva: yup.number().min(0, "Le TVA minimal est 0"),
  });

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={initialValues}
      validationSchema={productSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => (
        <form
          onSubmit={(e) => {
            handleSubmit(e);
          }}
        >
          <Box
            display="grid"
            gap="16px"
            gridTemplateColumns="repeat(6, minmax(0, 1fr))"
            sx={{
              maxWidth: 1080,
              "& > *": { minWidth: 0 },
              ...(!isNonMobile && {
                "& > *": { gridColumn: "span 6 !important" },
              }),
            }}
          >
            <Typography
              variant="h6"
              sx={{ gridColumn: "span 6", color: "text.secondary" }}
            >
              {t("fields.sectionDetails")}
            </Typography>
            <TextFieldCompnent
              type="text"
              label={t("fields.name")}
              change={handleChange}
              value={values.name}
              name="name"
              blur={handleBlur}
              touched={touched.name}
              error={errors.name}
              colum="span 6"
              required
            />
            <TextFieldCompnent
              multiline
              minRows={6}
              maxRows={8}
              type="text"
              label={t("fields.description")}
              change={handleChange}
              value={values.description}
              name="description"
              blur={handleBlur}
              touched={touched.description}
              error={errors.description}
              colum="span 4"
            />
            <ImageInput
              colum="span 2"
              previewImage={previewImage}
              setPreviewImage={setPreviewImage}
              image={image}
              mediaTargetType="Product"
            />
            <Typography
              variant="h6"
              sx={{ gridColumn: "span 6", color: "text.secondary", mt: 1 }}
            >
              {t("fields.sectionPricing")}
            </Typography>
            <TextFieldCompnent
              type="number"
              label={t("fields.price")}
              change={handleChange}
              value={values.price || 0}
              name="price"
              blur={handleBlur}
              touched={touched.price}
              error={errors.price}
              colum="span 2"
              num={0}
              required
            />
            <TextFieldCompnent
              type="number"
              label={t("fields.formulePrice")}
              change={handleChange}
              value={values.formulePrice || 0}
              name="formulePrice"
              blur={handleBlur}
              touched={touched.formulePrice}
              error={errors.formulePrice}
              colum="span 2"
              num={0}
            />
            <TextFieldCompnent
              type="number"
              label={t("fields.tvaOptional")}
              change={handleChange}
              value={values.tva}
              name="tva"
              blur={handleBlur}
              touched={touched.tva}
              error={errors.tva}
              colum="span 2"
              num={0}
            />
            <Typography
              variant="h6"
              sx={{ gridColumn: "span 6", color: "text.secondary", mt: 1 }}
            >
              {t("fields.sectionClassification")}
              <Box component="span" sx={{ color: "error.main" }}>
                {" "}
                *
              </Box>
            </Typography>
            {categories.length === 0 ? (
              <Box sx={{ gridColumn: "span 3" }}>
                <FormControl fullWidth disabled>
                  <InputLabel>{t("fields.noCategory")}</InputLabel>
                  <Select value="">
                    <MenuItem value="">{t("fields.noCategory")}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <SelectListUI
                name="categories"
                title={t("fields.selectCategories")}
                items={categories}
                value={values.categories}
                change={(e) => {
                  const categoryIDs = e.target.value.map((item) =>
                    typeof item === "object" && item !== null ? item._id : item
                  );
                  setFieldValue("categories", categoryIDs);
                }}
                gridColumn="span 3"
                error={errors.categories}
                touched={touched.categories}
                loading={false}
              />
            )}
            <SelectListUI
              name="allergies"
              title={t("fields.selectAllergies")}
              items={allergies}
              value={values.allergies}
              change={(e) => handleChange(e)}
              gridColumn="span 3"
              error={errors.allergies}
              touched={touched.allergies}
              loading={false}
            />
            <Typography
              variant="h6"
              sx={{ gridColumn: "span 6", color: "text.secondary", mt: 1 }}
            >
              {t("fields.sectionAvailability")}
            </Typography>
            <RadioButtonComponent
              change={handleChange}
              colum="span 2"
              name="outOfStock"
              radioText1={t("common.no")}
              radioText2={t("common.yes")}
              radioValue1={false}
              radioValue2={true}
              text={t("fields.outOfStock")}
              value={values.outOfStock}
            />
            <RadioButtonComponent
              change={handleChange}
              colum="span 2"
              name="visible"
              radioText1={t("common.no")}
              radioText2={t("common.yes")}
              radioValue1={false}
              radioValue2={true}
              text={t("fields.showItem")}
              value={values.visible}
            />
            {!isDialog && (
              <>
                <RadioButtonComponent
                  change={handleChange}
                  colum="span 2"
                  name="choice"
                  radioText1={t("fields.single")}
                  radioText2={t("fields.composed")}
                  radioValue1="seul"
                  radioValue2="multiple"
                  text={t("fields.productChoice")}
                  value={values.choice}
                  required
                />
                {values.choice === "multiple" && (
                  <>
                    <Typography
                      variant="h6"
                      sx={{ gridColumn: "span 6", color: "text.secondary", mt: 1 }}
                    >
                      {t("fields.sectionOptions")}
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      sx={{ gridColumn: "span 6" }}
                    >
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <FormControl fullWidth variant="filled">
                          <InputLabel id="type-select-label">
                            {t("fields.selectOptions")}
                          </InputLabel>
                          <Select
                            labelId="type-select-label"
                            multiple
                            value={selectedTypes.map((type) => type._id)}
                            onChange={handleTypeSelect}
                          >
                            {typesWithIngrediants.map((type) => (
                              <MenuItem
                                key={type._id}
                                value={type._id}
                                sx={{
                                  opacity: selectedTypes.some(
                                    (selectedType) =>
                                      selectedType._id === type._id
                                  )
                                    ? 1
                                    : 0.6,
                                  backgroundColor: selectedTypes.some(
                                    (selectedType) =>
                                      selectedType._id === type._id
                                  )
                                    ? "black !important"
                                    : "transparent",
                                  color: selectedTypes.includes(type._id)
                                    ? "white"
                                    : "inherit",
                                }}
                              >
                                {type.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Grid item>
                          <SelectComponent
                            name="type de variations"
                            label={t("fields.variationType")}
                            items={typeVariation}
                            value={values.typeVariation}
                            change={(e) => {
                              const selectedId = e.target.value;
                              if (selectedId === "") {
                                setFieldValue("typeVariation", "");
                                setFieldValue("variations", []);
                                return;
                              }
                              const selectedTypeVariation = typeVariation.find(
                                (tv) => tv._id === selectedId
                              );

                              if (selectedTypeVariation) {
                                setFieldValue("typeVariation", selectedId);
                                const variationsWithPrices =
                                  selectedTypeVariation.variations.map((v) => {
                                    return {
                                      typeVariation: selectedTypeVariation._id,
                                      variation: v._id,
                                      name: `${selectedTypeVariation.name} ${v.name}`,
                                      price: 0,
                                    };
                                  });
                                setFieldValue(
                                  "variations",
                                  variationsWithPrices
                                );
                              }
                            }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={6}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {values.variations.map((variation, index) => (
                            <TextFieldCompnent
                              key={`${variation.variation}-${index}`}
                              type="number"
                              label={t("fields.priceFor", { name: variation.name })}
                              change={(e) => {
                                const newVariations = [...values.variations];
                                newVariations[index].price = Number(
                                  e.target.value
                                );
                                handleChange({
                                  target: {
                                    name: "variations",
                                    value: newVariations,
                                  },
                                });
                              }}
                              value={variation.price}
                              name={`variation-${variation.variation}-price`}
                              blur={handleBlur}
                              colum="span 2"
                              num={0}
                            />
                          ))}
                        </Grid>
                      </Grid>
                      {selectedTypes.length > 0 && (
                        <Grid item xs={12} md={4}>
                          <Reorder
                            items={selectedTypes}
                            onReorder={(newOrder) => {
                              setSelectedTypes(newOrder);
                              setFieldValue(
                                "types",
                                newOrder.map((type) => type._id)
                              );
                            }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </>
            )}
            <Typography
              variant="h6"
              sx={{ gridColumn: "span 6", color: "text.secondary", mt: 1 }}
            >
              {t("fields.sectionDiscount")}
            </Typography>
            <TextFieldCompnent
              type="number"
              label={t("fields.discountValue", { currency })}
              change={handleChange}
              value={values.discountValue || 0}
              name="discountValue"
              blur={handleBlur}
              touched={touched.discountValue}
              error={errors.discountValue}
              colum="span 2"
              num={0}
            />
            <DatepickerFilter
              label={t("fields.startDateOptional")}
              onChange={(newValue) => {
                setFieldValue(
                  "discountStartDate",
                  newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                );
              }}
              value={values.discountStartDate ? values.discountStartDate : ""}
              name="discountStartDate"
              handleBlur={handleBlur}
              touched={touched.discountStartDate}
              error={errors.discountStartDate}
              column="span 2"
              maxDate={
                values.discountEndDate &&
                dayjs(values.discountEndDate).isBefore(dayjs())
                  ? dayjs(values.discountEndDate)
                  : null
              }
              minDate={dayjs()}
            />
            <DatepickerFilter
              label={t("fields.endDateOptional")}
              onChange={(newValue) => {
                setFieldValue(
                  "discountEndDate",
                  newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                );
              }}
              value={values.discountEndDate ? values.discountEndDate : ""}
              name="discountEndDate"
              handleBlur={handleBlur}
              touched={touched.discountEndDate}
              error={errors.discountEndDate}
              column="span 2"
              minDate={
                values.discountStartDate
                  ? dayjs(values.discountStartDate)
                  : dayjs()
              }
            />
          </Box>
          <ButtonComponent text={buttonText} loading={loading} />
        </form>
      )}
    </Formik>
  );
};

export default ProductForm;
